/**
 * Inc2 · Instrumentos de recolección (`/api/instrumentos`).
 *
 * Máquina de estados edicion → aprobado → asignado → completado. `asignado` es el
 * paso propio de opinión; documental y cuantitativa pueden saltárselo. Al
 * completarse, el instrumento entra en el repositorio reutilizable.
 *
 * El campo `tipo` se expone como `herramienta` y es texto libre por datos
 * históricos: hay que normalizarlo antes de compararlo.
 *
 * Capa de servicios: una función por operación del API. No lanza nunca —cada
 * función devuelve un valor neutro ante el fallo—, así que el llamador debe
 * comprobar el resultado, no envolver en try/catch.
 */

import type Instrumento from '@/interfaces/Instrumetos'
import type { CatalogoInstrumento } from '@/interfaces/Instrumetos'
import { http } from '@/services/http'

/**
 * Catálogo de herramientas (encuesta, grupo focal, taller…) y modalidades.
 * Lo sirve el backend para no cablear la lista en el cliente: de ahí sale
 * también qué tipos de pregunta admite cada herramienta.
 */
export async function getCatalogoInstrumentosService(): Promise<CatalogoInstrumento> {
  const result = await http.get<CatalogoInstrumento>('/api/instrumentos/herramientas')
  return result.ok && result.data ? result.data : { herramientas: [], modalidades: [] }
}

/**
 * Crea instrumentos · petición `POST`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `null`. Un null
 * NO distingue «no encontrado» de «la petición falló».
 *
 * @returns Instrumento | null
 */
export async function crearInstrumentoService(
  url: string,
  instrumento: Omit<Instrumento, 'id_instrumento'> & { fuente?: string; id_grupo_interes?: number; id_version?: number }
): Promise<Instrumento | null> {
  const result = await http.post<Instrumento>(url, instrumento)
  return result.ok ? (result.data ?? null) : null
}

/**
 * Consulta instrumentos · petición `GET`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `[]`. Un array
 * vacío NO distingue «sin datos» de «la petición falló».
 *
 * @returns Instrumento[]
 */
export async function getInstrumentosService(url: string): Promise<Instrumento[]> {
  const result = await http.get<any>(url)
  if (!result.ok) return []
  const d = result.data
  return Array.isArray(d) ? d : (d?.data ?? [])
}

/**
 * Consulta instrumentos · petición `GET`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `null`. Un null
 * NO distingue «no encontrado» de «la petición falló».
 *
 * @returns Instrumento | null
 */
export async function getInstrumentoByIdService(url: string, id: number): Promise<Instrumento | null> {
  const result = await http.get<Instrumento>(`${url}/${id}`)
  return result.ok ? (result.data ?? null) : null
}

/**
 * Actualiza instrumentos · petición `PUT`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `null`. Un null
 * NO distingue «no encontrado» de «la petición falló».
 *
 * @returns Instrumento | null
 */
export async function actualizarInstrumentoService(url: string, id: number, payload: Partial<Instrumento>): Promise<Instrumento | null> {
  const result = await http.put<Instrumento>(`${url}/${id}`, payload)
  return result.ok ? (result.data ?? null) : null
}

/**
 * Elimina instrumentos · petición `DELETE`.
 *
 * No lanza: `true` solo si la petición tuvo éxito. `false` agrupa todos los
 * fallos posibles sin distinguirlos.
 *
 * @returns boolean
 */
export async function eliminarInstrumentoService(url: string, id: number): Promise<boolean> {
  const result = await http.delete(`${url}/${id}`)
  return result.ok
}

/**
 * Consulta instrumentos · petición `GET`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `null`. Un null
 * NO distingue «no encontrado» de «la petición falló».
 *
 * @returns any | null
 */
export async function getDependenciasInstrumentoService(url: string, id: number): Promise<any | null> {
  const result = await http.get<any>(`${url}/${id}/dependencias`)
  return result.ok ? (result.data ?? null) : null
}

/**
 * Consulta instrumentos · petición `GET`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `null`. Un null
 * NO distingue «no encontrado» de «la petición falló».
 *
 * @returns any[] | null
 */
export async function getPreguntasPorGrupoService(url: string, id: number): Promise<any[] | null> {
  const result = await http.get<any[]>(`${url}/${id}/preguntas-por-grupo`)
  return result.ok ? (result.data ?? null) : null
}

/**
 * Elimina instrumentos · petición `DELETE`.
 *
 * No lanza: `true` solo si la petición tuvo éxito. `false` agrupa todos los
 * fallos posibles sin distinguirlos.
 *
 * @returns boolean
 */
export async function eliminarInstrumentoForceService(url: string, id: number): Promise<boolean> {
  const result = await http.delete(`${url}/${id}/force`)
  return result.ok
}

/**
 * Cambia instrumentos · petición `PATCH`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `null`. Un null
 * NO distingue «no encontrado» de «la petición falló».
 *
 * @returns Instrumento | null
 */
export async function cambiarEstadoInstrumentoService(
  url: string,
  id: number,
  estado: 'edicion' | 'aprobado' | 'asignado' | 'completado'
): Promise<Instrumento | null> {
  const result = await http.patch<Instrumento>(`${url}/${id}/estado`, { estado })
  return result.ok ? (result.data ?? null) : null
}

/**
 * Operación sobre instrumentos · petición `POST`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `null`. Un null
 * NO distingue «no encontrado» de «la petición falló».
 *
 * @returns any | null
 */
export async function aplicarInstrumentoService(
  url: string,
  id: number,
  payload: { id_ejecucion: number; ambito: string; id_programa?: number | null; periodo: string }
): Promise<any | null> {
  const result = await http.post<any>(`${url}/${id}/aplicar`, payload)
  return result.ok ? (result.data ?? null) : null
}

/**
 * Consulta instrumentos · petición `GET`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `[]`. Un array
 * vacío NO distingue «sin datos» de «la petición falló».
 *
 * @returns Instrumento[]
 */
export async function getRepositorioInstrumentosService(url: string): Promise<Instrumento[]> {
  const result = await http.get<Instrumento[]>(`${url}/repositorio`)
  return result.ok ? (result.data ?? []) : []
}

/**
 * Operación sobre instrumentos · petición `POST`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `null`. Un null
 * NO distingue «no encontrado» de «la petición falló».
 *
 * @returns Instrumento | null
 */
export async function reutilizarInstrumentoService(url: string, id: number, id_indicador: number): Promise<Instrumento | null> {
  const result = await http.post<Instrumento>(`${url}/${id}/reutilizar`, { id_indicador })
  return result.ok ? (result.data ?? null) : null
}
